import React, { useRef, useEffect, useState } from 'react';
import { useAppStore } from "../../../GlobalStore/GlobalStore.ts";

interface ReportPopupProps {}

const ReportPopup: React.FC<ReportPopupProps> = () => {
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);
    const modalRef = useRef<HTMLDivElement>(null);
    const [selectedReason, setSelectedReason] = useState<string | null>(null);
    const [otherReason, setOtherReason] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleClose = () => {
        setDynamicStateObj({ visiblePopup: null });
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
            handleClose();
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleReasonSelect = (reason: string) => {
        setSelectedReason(reason);
    };

    const handleSubmit = () => {
        // In a real app, you would send the report to your backend here
        console.log("Report submitted:", selectedReason === "Other" ? otherReason : selectedReason);

        // Show submission confirmation
        setIsSubmitted(true);

        // Close popup after 2 seconds
        setTimeout(() => {
            handleClose();
        }, 2000);
    };

    const isSubmitEnabled = selectedReason && (selectedReason !== "Other" || otherReason.trim().length > 0);

    const reportReasons = [
        "Offensive Language",
        "Inappropriate Content",
        "Misleading Information",
        "Other"
    ];

    return (
        <div className="fixed inset-0 bg-neutral-100/75 dark:bg-neutral-900/75 flex items-center justify-center backdrop-blur-sm p-4 z-50">
            <div ref={modalRef} className="bg-white dark:bg-zinc-800 rounded-xl shadow-2xl w-[98%] sm:w-11/12 md:w-2/3 lg:w-1/2 xl:w-2/5 max-w-md overflow-hidden border border-neutral-300 dark:border-neutral-700 relative">
                {isSubmitted ? (
                    <div className="p-8 text-center">
                        <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-medium text-neutral-800 dark:text-neutral-200 mb-2">Report Submitted</h3>
                        <p className="text-neutral-600 dark:text-neutral-400">Thank you for your feedback.</p>
                    </div>
                ) : (
                    <>
                        <div className="absolute top-0 left-0 w-full h-1 bg-red-500"></div>
                        <button
                            onClick={handleClose}
                            className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <div className="p-6">
                            <h2 className="text-2xl font-bold text-neutral-800 dark:text-neutral-200 mb-4">
                                Report Content
                            </h2>
                            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                                Please select a reason for your report:
                            </p>
                            <div className="space-y-3 mb-6">
                                {reportReasons.map((reason) => (
                                    <div
                                        key={reason}
                                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                                            selectedReason === reason 
                                                ? 'bg-red-500 text-white' 
                                                : 'border border-neutral-300 dark:border-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-700/50 text-neutral-700 dark:text-neutral-300'
                                        }`}
                                        onClick={() => handleReasonSelect(reason)}
                                    >
                                        <div className="flex items-center">
                                            <div className={`w-4 h-4 rounded-full flex-shrink-0 mr-3 ${
                                                selectedReason === reason 
                                                    ? 'border-2 border-white' 
                                                    : 'border border-neutral-400 dark:border-neutral-600'
                                            }`}>
                                                {selectedReason === reason && (
                                                    <div className="w-2 h-2 bg-white rounded-full m-auto"></div>
                                                )}
                                            </div>
                                            <span>{reason}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selectedReason === "Other" && (
                                <div className="mb-6">
                                    <label htmlFor="otherReason" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                                        Please specify:
                                    </label>
                                    <textarea
                                        id="otherReason"
                                        rows={3}
                                        className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-zinc-900 text-neutral-800 dark:text-neutral-200"
                                        placeholder="Please describe the issue..."
                                        value={otherReason}
                                        onChange={(e) => setOtherReason(e.target.value)}
                                    ></textarea>
                                </div>
                            )}

                            <div className="flex justify-end">
                                <button
                                    onClick={handleSubmit}
                                    disabled={!isSubmitEnabled}
                                    className={`px-4 py-2 rounded-lg font-medium ${
                                        isSubmitEnabled 
                                            ? 'bg-red-500 text-white hover:bg-red-600' 
                                            : 'bg-neutral-300 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-500 cursor-not-allowed'
                                    }`}
                                >
                                    Submit Report
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ReportPopup;